/**
 * When the native location tracker may start (card `t_88c3d858`).
 *
 * The tracker used to start only from `App.vue`'s boot path, and only when
 * that boot found the hub session already authenticated. On the flow the
 * owner's phone actually uses — the app boots on the launcher, the hub is
 * picked (or its cloud relay is opened) AFTER the mount — boot auth is false
 * (`stores/auth.ts` → `checkAuth()`: there is no local hub token, and
 * Capacitor ships no `apiUrl` until a hub is selected), so `startTracking()`
 * was never reached for the rest of the session. The start now follows the
 * session, and this is the one gate both spenders ask.
 *
 * Fail closed: nothing starts the tracker unless a session this device can
 * report through is live.
 */

import type { RuntimeConfig } from '../config/runtime';

/** The facts the gate reads — each one the app's own, taken as given. */
export interface LocationTrackingFacts {
  /** This window holds a hub session: `authStore.isAuthenticated` (a hub JWT). */
  authenticated: boolean;
  /** A cloud relay session is live: `webrtcStore.connected`. */
  webrtcConnected: boolean;
  /** Panel mode never tracks — a panel is a fixed installation. */
  panelMode: boolean;
  /** The runtime mode (`getRuntimeConfig().mode`); cloud-web ('cloud') never tracks. */
  mode: RuntimeConfig['mode'];
}

/**
 * A session is live when this window either holds a hub session of its own (a
 * LAN claim, a sign-in, a restored boot session — `authenticated`) or a cloud
 * relay session is up (`webrtcConnected`: in the relay flow the hub JWT is
 * injected per request, so `isAuthenticated` stays false and the tunnel is the
 * whole session).
 */
export function shouldStartLocationTracking(facts: LocationTrackingFacts): boolean {
  if (facts.panelMode) return false;
  if (facts.mode === 'cloud') return false;
  return facts.authenticated || facts.webrtcConnected;
}
